import { gql, useQuery } from "@apollo/client";
import { eachDayOfInterval, parse } from "date-fns";
import { useCallback, useMemo } from "react";
import { MAX_BOOKING_MONTHS_IN_ADVANCE } from "../core/availability";
import { Product, ProductAvailability } from "../graphql/operations";
import { addDuration, formatDate } from "../utils/calendarUtils";
import { PRODUCT_AVAILABILITY_FRAGMENT } from "../utils/queries/ProductAvailability";

type UseAvailabilityProps = {
  productId: Product["id"];
  from?: Date;
  until?: Date;
  duration?: Parameters<typeof addDuration>[1];
  isPrivate?: boolean;
  groupSize?: number;
  preloadedAvailability?: ProductAvailability[];
};

export default function useAvailability(props: UseAvailabilityProps) {
  const from = useMemo(() => props.from ?? new Date(), [props.from]);

  const until = useMemo(
    () =>
      props.until ??
      addDuration(
        from,
        props.duration ?? { months: MAX_BOOKING_MONTHS_IN_ADVANCE }
      ),
    [from, props.duration, props.until]
  );

  const { loading, error, data } = useQuery<GetAvailabilityResponse>(
    GET_AVAILABILITY,
    {
      ssr: false,
      fetchPolicy: "cache-and-network",
      variables: {
        productId: props.productId,
        fromDate: formatDate(from),
        untilDate: formatDate(until),
      },
    }
  );

  const availabilities = useMemo(
    () => data?.product.availability ?? props.preloadedAvailability ?? [],
    [props.preloadedAvailability, data]
  );

  const loadedRange = useMemo(
    () =>
      availabilities.length
        ? {
            from: parse(availabilities[0].date, "yyyy-MM-dd", new Date()),
            until: parse(
              availabilities[availabilities.length - 1].date,
              "yyyy-MM-dd",
              new Date()
            ),
          }
        : undefined,
    [availabilities]
  );

  const availabilityMap = useMemo(
    () => new Map(availabilities.map(({ date, ...rest }) => [date, rest])),
    [availabilities]
  );

  const isAvailableInRange = useCallback(
    (props: {
      from?: Date;
      until?: Date;
      duration?: Parameters<typeof addDuration>[1];
    }) =>
      !!eachDayOfInterval({
        start: props.from ?? new Date(),
        end: props.until ?? addDuration(from, props.duration ?? { years: 1 }),
      }).find(
        (date) => availabilityMap.get(formatDate(date))?.status === "AVAILABLE"
      ),
    [availabilityMap, from]
  );

  const isAvailableInYear = useMemo(
    () => isAvailableInRange({ from, until }),
    [from, until, isAvailableInRange]
  );

  const getTimeSlots = useCallback(
    (date: Date | string) =>
      availabilityMap.get(formatDate(date))?.timeSlots ?? [],
    [availabilityMap]
  );

  const getGroups = useCallback(
    (date: Date | string, time: string) =>
      getTimeSlots(date).find((timeSlot) => timeSlot.time === time)?.groups ??
      [],
    [getTimeSlots]
  );

  const isTimeSlotAvailable = useCallback(
    (date, time) => {
      const timeSlot =
        typeof time === "string"
          ? getTimeSlots(date).find((timeSlot) => timeSlot.time === time)
          : time;

      if (timeSlot?.status !== "AVAILABLE")
        return { available: false, unavailableReason: "Full" };

      return (
        timeSlot.groups
          .map((group) =>
            isGroupAvailable(group, props.groupSize ?? 1, props.isPrivate)
          )
          .sort((a, b) =>
            (a.unavailableReason ?? "").localeCompare(b.unavailableReason ?? "")
          )[0] ?? { available: false }
      );
    },
    [props.groupSize, props.isPrivate, getTimeSlots]
  );

  const isDateSelectable = useCallback(
    (availability: ProductAvailability) =>
      availability.status === "AVAILABLE" &&
      availability.timeSlots.some(
        (timeSlot) => isTimeSlotAvailable(availability.date, timeSlot).available
      ),
    [isTimeSlotAvailable]
  );

  const isDateAvailable = useCallback(
    (date) => {
      const availability = { date, ...availabilityMap.get(formatDate(date)) };
      return isDateSelectable(availability);
    },
    [isDateSelectable, availabilityMap]
  );

  const getAvailableTimeSlots = useCallback(
    (date: string | Date) =>
      getTimeSlots(date).filter(
        (timeSlot) => isTimeSlotAvailable(date, timeSlot.time).available
      ),
    [getTimeSlots, isTimeSlotAvailable]
  );

  const getAvailableGroups = useCallback(
    (date: string | Date, time: string) =>
      getGroups(date, time).filter(
        (group) =>
          isGroupAvailable(group, props.groupSize, props.isPrivate).available
      ),
    [props.groupSize, props.isPrivate, getGroups]
  );

  const availableDates = useMemo(
    () => availabilities.filter(isDateSelectable),
    [availabilities, isDateSelectable]
  );

  const productTimeZone = data?.product.location.timezone ?? "Asia/Tokyo";

  const availableDatesAsDate = useMemo(
    () =>
      availableDates.map(
        ({ date }) =>
          new Date(
            new Date(date).toLocaleString("en-US", {
              timeZone: productTimeZone,
            })
          )
      ),
    [availableDates]
  );

  const availabilitiesAsDates = useMemo(
    () =>
      availabilities.map(
        ({ date }) =>
          new Date(
            new Date(date).toLocaleString("en-US", {
              timeZone: productTimeZone,
            })
          )
      ),
    [availabilities]
  );

  return {
    availabilities,
    availabilityMap,
    isAvailableInYear,
    isAvailableInRange,
    isDateAvailable,
    isTimeSlotAvailable,
    availableDates,
    availableDatesAsDate,
    availabilitiesAsDates,
    getTimeSlots,
    getAvailableTimeSlots,
    getGroups,
    getAvailableGroups,
    loadedRange,
    loading,
    error,
  };
}

type GetAvailabilityResponse = {
  product: Pick<Product, "id" | "availability" | "location">;
};
export const GET_AVAILABILITY = gql`
  query AvailabilityQuery(
    $productId: ID!
    $fromDate: Date!
    $untilDate: Date!
  ) {
    product(id: $productId) {
      id
      location {
        timezone
      }
      availability(fromDate: $fromDate, untilDate: $untilDate) {
        ...AvailabilityFragment
      }
    }
  }
  ${PRODUCT_AVAILABILITY_FRAGMENT}
`;

export const isGroupAvailable = (group, groupSize, isPrivate?: boolean) => {
  const unavailableReason = group.isPrivate
    ? "Group is Private"
    : isPrivate && !group.isEmpty
    ? "No Private Slot"
    : group.seatsAvailable < (groupSize ?? 1)
    ? "Not Enough Seats"
    : undefined;

  return {
    available: !unavailableReason,
    unavailableReason,
  };
};
